/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';

import { combineReducers } from 'redux';
import SeamlessImmutable from 'seamless-immutable';
import { pick, isObject, findIndex } from 'underscore';
import { PERCENT, PIXELS } from 'DesignData/enums/SizeUnits';
import { SIDEBAR_WIDTH } from 'DesignData/constants/PaneSizes';
import { CACHE_MAX_ITEMS } from 'DesignData/constants/LocalStorage';
import { EMPTY_OBJECT } from 'DesignData/lib/object';
import { isExpired, createUpdatedTimestampForMissing } from 'DesignData/lib/localStorage';
import { getTimestampForAction } from 'DesignData/lib/actions';
import PaneSize from 'DesignData/models/PaneSize';
import { CODE_PANES, SIDEBAR, HTML, CSS, JS } from 'DesignData/enums/CustomWidgetEditorPaneTypes';
import { CUSTOM_WIDGET_EDITOR_OPTIONS_FETCH, CUSTOM_WIDGET_EDITOR_OPTIONS_HYDRATE, CUSTOM_WIDGET_EDITOR_OPTIONS_UPDATE_PANE, CUSTOM_WIDGET_EDITOR_OPTIONS_SET_SELECTED_FIELD, CUSTOM_WIDGET_FIELD_CREATE, CUSTOM_WIDGET_FIELD_CREATE_GROUP, CUSTOM_WIDGET_FIELD_CLONE, CUSTOM_WIDGET_EDITOR_OPTIONS_TOGGLE_FIELD_GROUPING, CUSTOM_WIDGET_EDITOR_OPTIONS_TOGGLE_FIELD_GROUPING_SELECTION, CUSTOM_WIDGET_EDITOR_OPTIONS_SET_IS_ICON_SELECTING, CUSTOM_WIDGET_EDITOR_OPTIONS_SET_IS_EDITING_GLOBAL_CONTENT, CUSTOM_WIDGET_EDITOR_OPTIONS_SET_FULL_SCREEN_EDITOR_OPEN, CUSTOM_WIDGET_EDITOR_OPTIONS_SAVE_ACCORDION_STATE, CUSTOM_WIDGET_EDITOR_OPTIONS_SET_LANGUAGE_EDITOR, CUSTOM_WIDGET_EDITOR_OPTIONS_SELECT_SMART_RULE, CUSTOM_WIDGET_BUFFER_ADD_SMART_RULE, CUSTOM_WIDGET_BUFFER_REMOVE_SMART_RULE, CUSTOM_WIDGET_BUFFER_REMOVE_SMART_RULES, CUSTOM_WIDGET_BUFFER_MAKE_SMART, CUSTOM_WIDGET_BUFFERS_UPDATE_SMART_RULES } from 'DesignData/actions/ActionTypes';
import { STYLE } from 'ContentUtils/constants/CustomWidgetFieldGroupTabTypes';
const DEFAULT_PANES = {
  [CODE_PANES]: {},
  [SIDEBAR]: {
    size: new PaneSize({
      value: SIDEBAR_WIDTH,
      units: PIXELS
    })
  },
  [HTML]: {
    open: true,
    size: new PaneSize({
      value: 50,
      units: PERCENT
    })
  },
  [CSS]: {
    open: true,
    size: new PaneSize({
      value: 25,
      units: PERCENT
    })
  },
  [JS]: {
    open: true,
    size: new PaneSize({
      value: 25,
      units: PERCENT
    })
  }
};
const DEFAULT_PANES_LIST = Object.keys(DEFAULT_PANES);
const CODE_PANE_KEYS = [HTML, CSS, JS];

function paneSizeConvert(size) {
  const value = size.value;
  const units = size.units;
  return new PaneSize({
    value,
    units
  });
}

const DEFAULT_FIELD_GROUPING = {
  isActive: false,
  fieldType: 'CONTENT',
  fieldsAreNotSiblings: false,
  isTooDeep: false,
  selectedFields: {}
}; // Reset code pane sizes when opening the sole collapsed pane

function codePanesShouldReset(state, changes) {
  const closedCodePanes = CODE_PANE_KEYS.filter(key => !state[key].open);

  if (closedCodePanes.length !== 1) {
    return false;
  }

  const paneChanges = changes[closedCodePanes[0]] || {};
  return paneChanges.open && !paneChanges.size;
}

function panes(state = SeamlessImmutable(DEFAULT_PANES), action) {
  switch (action.type) {
    case CUSTOM_WIDGET_EDITOR_OPTIONS_UPDATE_PANE:
      {
        const changes = action.options || {};

        if (codePanesShouldReset(state, changes)) {
          const defaultCodePanes = pick(DEFAULT_PANES, CODE_PANE_KEYS);
          state = SeamlessImmutable.merge(state, defaultCodePanes, {
            deep: true
          });
        } else {
          state = SeamlessImmutable.merge(state, changes, {
            deep: true
          });
        }

        state = SeamlessImmutable.set(state, 'updated', getTimestampForAction(action));
        return state;
      }

    default:
      return state;
  }
}
/*
 * {
 *   1234: {SIDEBAR: {size: {value: 320, units: PIXELS}}, HTML: {open: false}},
 * }
 */


function panesById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case CUSTOM_WIDGET_EDITOR_OPTIONS_FETCH:
      {
        if (!action.options) return state;
        const {
          panesById: byId
        } = action.options;
        if (!(byId && isObject(byId))) return state;
        const orderedList = [];
        const newState = {};
        Object.keys(byId).forEach(customWidgetId => {
          const data = byId[customWidgetId];

          if (data.updated) {
            if (isExpired(data)) return;
          } else {
            data.updated = createUpdatedTimestampForMissing();
          }

          DEFAULT_PANES_LIST.forEach(paneType => {
            const current = data && data[paneType];

            if (!current) {
              data[paneType] = DEFAULT_PANES[paneType];
              return;
            }

            const {
              size
            } = data[paneType];

            if (size && !(size instanceof PaneSize)) {
              data[paneType].size = paneSizeConvert(size);
            }
          });
          newState[customWidgetId] = data; // Insert in list by timestamp in DESC order.

          const idx = findIndex(orderedList, ([, updated]) => updated <= data.updated);

          if (!~idx) {
            orderedList.push([customWidgetId, data.updated]);
          } else {
            orderedList.splice(idx, 0, [customWidgetId, data.updated]);
          }
        }); // Remove data

        if (orderedList.length > CACHE_MAX_ITEMS) {
          orderedList.slice(CACHE_MAX_ITEMS).forEach(([customWidgetId]) => {
            delete newState[customWidgetId];
          });
        }

        return SeamlessImmutable.merge(state, newState, {
          deep: true
        });
      }

    case CUSTOM_WIDGET_EDITOR_OPTIONS_HYDRATE:
    case CUSTOM_WIDGET_EDITOR_OPTIONS_UPDATE_PANE:
      {
        const {
          customWidgetId
        } = action;
        return state.set(customWidgetId, panes(state[customWidgetId], action));
      }

    default:
      {
        return state;
      }
  }
}

function selectedField(state = SeamlessImmutable({
  isNew: false
}), action) {
  switch (action.type) {
    case CUSTOM_WIDGET_EDITOR_OPTIONS_SET_SELECTED_FIELD:
      {
        const {
          id
        } = action.fieldObj || {};
        return state.merge({
          id,
          isNew: false
        });
      }

    case CUSTOM_WIDGET_FIELD_CREATE:
      {
        const {
          field
        } = action;
        return state.merge({
          id: field.id,
          isNew: true
        });
      }

    case CUSTOM_WIDGET_FIELD_CREATE_GROUP:
      {
        const {
          fieldGroup
        } = action;
        return state.merge({
          id: fieldGroup.id,
          isNew: true
        });
      }

    case CUSTOM_WIDGET_FIELD_CLONE:
      {
        const {
          clonedField
        } = action;
        return state.merge({
          id: clonedField.id,
          isNew: true
        });
      }

    default:
      {
        return state;
      }
  }
}
/*
 * {
 *   1234: {id: 'af564474-5c82-7a18-5166-60d53c57e773', isNew: false},
 * }
 */


function selectedFieldById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case CUSTOM_WIDGET_EDITOR_OPTIONS_FETCH:
      {
        if (!action.options) return state;
        const {
          selectedFieldById: byId
        } = action.options;
        if (!(byId && isObject(byId))) return state;
        const newState = {};
        Object.keys(byId).forEach(customWidgetId => {
          const data = byId[customWidgetId];

          if (data.updated) {
            if (isExpired(data)) return;
          } else {
            data.updated = createUpdatedTimestampForMissing();
          }

          newState[customWidgetId] = data;
        });
        return SeamlessImmutable(newState);
      }

    case CUSTOM_WIDGET_EDITOR_OPTIONS_SET_SELECTED_FIELD:
    case CUSTOM_WIDGET_FIELD_CREATE:
    case CUSTOM_WIDGET_FIELD_CREATE_GROUP:
    case CUSTOM_WIDGET_FIELD_CLONE:
      {
        const {
          customWidgetId,
          fieldGroup
        } = action;

        if (fieldGroup && fieldGroup.tab === STYLE) {
          return state;
        }

        state = SeamlessImmutable.set(state, customWidgetId, selectedField(state[customWidgetId], action));
        state = SeamlessImmutable.setIn(state, [customWidgetId, 'updated'], getTimestampForAction(action));
        return state;
      }

    default:
      {
        return state;
      }
  }
}

function fieldGrouping(state = SeamlessImmutable(DEFAULT_FIELD_GROUPING), action) {
  switch (action.type) {
    case CUSTOM_WIDGET_EDITOR_OPTIONS_TOGGLE_FIELD_GROUPING:
      {
        const {
          fieldType
        } = action;
        return state.merge(Object.assign({}, DEFAULT_FIELD_GROUPING, {
          isActive: !state.isActive,
          fieldType
        }));
      }

    case CUSTOM_WIDGET_EDITOR_OPTIONS_TOGGLE_FIELD_GROUPING_SELECTION:
      {
        const {
          selectedFields,
          fieldsAreNotSiblings,
          isTooDeep
        } = action;
        return state.merge({
          selectedFields,
          fieldsAreNotSiblings,
          isTooDeep
        });
      }

    case CUSTOM_WIDGET_FIELD_CREATE_GROUP:
      {
        return state.merge(DEFAULT_FIELD_GROUPING);
      }

    default:
      {
        return state;
      }
  }
}
/*
 * {
 *   1234: {
      isActive: true,
      fieldsAreNotSiblings: false,
      isTooDeep: false,
      selectedFields: {
        'af564474-5c82-7a18-5166-60d53c57e77': CustomWidgetField
      }
    },
 * }
 */


function fieldGroupingById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case CUSTOM_WIDGET_FIELD_CREATE_GROUP:
    case CUSTOM_WIDGET_EDITOR_OPTIONS_TOGGLE_FIELD_GROUPING:
    case CUSTOM_WIDGET_EDITOR_OPTIONS_TOGGLE_FIELD_GROUPING_SELECTION:
      {
        const {
          customWidgetId
        } = action;
        return state.set(customWidgetId, fieldGrouping(state[customWidgetId], action));
      }

    default:
      {
        return state;
      }
  }
}

function isSelectingIconById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case CUSTOM_WIDGET_EDITOR_OPTIONS_SET_IS_ICON_SELECTING:
      {
        const {
          customWidgetId,
          isSelecting
        } = action;
        return state.set(customWidgetId, isSelecting);
      }

    default:
      {
        return state;
      }
  }
}

function isEditingGlobalContentById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case CUSTOM_WIDGET_EDITOR_OPTIONS_SET_IS_EDITING_GLOBAL_CONTENT:
      {
        const {
          customWidgetId,
          isEditingGlobalContent
        } = action;
        return state.set(customWidgetId, isEditingGlobalContent);
      }

    default:
      {
        return state;
      }
  }
}

function selectedSmartRuleIdById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case CUSTOM_WIDGET_EDITOR_OPTIONS_SELECT_SMART_RULE:
      {
        const {
          customWidgetId,
          smartRuleId
        } = action;
        return state.set(customWidgetId, smartRuleId);
      }

    case CUSTOM_WIDGET_BUFFER_ADD_SMART_RULE:
      {
        const {
          customWidgetId,
          smartObject
        } = action;
        return state.set(customWidgetId, smartObject.criterion_id);
      }

    case CUSTOM_WIDGET_BUFFER_REMOVE_SMART_RULE:
      {
        const {
          customWidgetId,
          smartRuleId
        } = action;

        if (smartRuleId === state[customWidgetId]) {
          return state.set(customWidgetId, null);
        }

        return state;
      }

    case CUSTOM_WIDGET_BUFFER_REMOVE_SMART_RULES:
      {
        const {
          customWidgetId
        } = action;
        return state.set(customWidgetId, null);
      }

    case CUSTOM_WIDGET_BUFFERS_UPDATE_SMART_RULES:
      {
        const {
          customWidgetId
        } = action;
        return state.set(customWidgetId, null);
      }

    case CUSTOM_WIDGET_BUFFER_MAKE_SMART:
      {
        const {
          customWidgetId,
          smartObjectData
        } = action;

        if (smartObjectData && smartObjectData.smartObjects && smartObjectData.smartObjects.length) {
          const smartRule = smartObjectData.smartObjects[0];
          return state.set(customWidgetId, smartRule.criterion_id);
        }

        return state;
      }

    default:
      {
        return state;
      }
  }
}

function isFullScreenEditorOpenById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case CUSTOM_WIDGET_EDITOR_OPTIONS_SET_FULL_SCREEN_EDITOR_OPEN:
      {
        const {
          customWidgetId,
          open
        } = action;
        return state.set(customWidgetId, open);
      }

    default:
      {
        return state;
      }
  }
}

function accordionStatesById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case CUSTOM_WIDGET_EDITOR_OPTIONS_FETCH:
      {
        if (!action.options) return state;
        const {
          accordionStatesById: byId
        } = action.options;

        if (!(byId && isObject(byId))) {
          return state;
        }

        const newState = {};
        Object.keys(byId).forEach(customWidgetId => {
          const data = byId[customWidgetId];

          if (data.updated) {
            if (isExpired(data)) return;
          } else {
            data.updated = createUpdatedTimestampForMissing();
          }

          newState[customWidgetId] = data;
        });
        return SeamlessImmutable(newState);
      }

    case CUSTOM_WIDGET_EDITOR_OPTIONS_SAVE_ACCORDION_STATE:
      {
        const {
          customWidgetId,
          fieldId,
          isOpen,
          key
        } = action;
        if (!(customWidgetId && key)) return state;
        state = fieldId ? SeamlessImmutable.setIn(state, [customWidgetId, fieldId, key], isOpen) : SeamlessImmutable.setIn(state, [customWidgetId, key], isOpen);
        state = SeamlessImmutable.setIn(state, [customWidgetId, 'updated'], getTimestampForAction(action));
        return state;
      }

    default:
      {
        return state;
      }
  }
}

function selectedLanguageEditorOpenById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case CUSTOM_WIDGET_EDITOR_OPTIONS_SET_LANGUAGE_EDITOR:
      {
        const {
          customWidgetId,
          lang
        } = action;
        return state.set(customWidgetId, lang);
      }

    default:
      return state;
  }
}

export default combineReducers({
  panesById,
  selectedFieldById,
  fieldGroupingById,
  isSelectingIconById,
  isEditingGlobalContentById,
  selectedSmartRuleIdById,
  isFullScreenEditorOpenById,
  accordionStatesById,
  selectedLanguageEditorOpenById
});